import React, { useState, useCallback, useMemo } from 'react'
import { GridApi } from 'ag-grid-enterprise'
import { AllocationEntityType } from '../types'
import { StatementColumn, WithBalanceRegistries } from '../../financialStatements/types'
import { BudgetAllocationModal } from '../components/BudgetAllocationModal'
import { hasRegistryData, hasRegistryGroups } from '../../../shared/utils'
import { convertAccountRowToAllocationRows, convertMonthlyBudgetsToAccountData } from '../utils/accountDataConversion'
import { convertDriverDataToAllocationRows, convertMonthlyBudgetsToFormulaData } from '../utils/driverDataConversion'

interface UseBudgetAllocationProps<TRow extends WithBalanceRegistries> {
  /**
   * AG Grid API instance
   */
  gridApi?: GridApi<TRow>

  /**
   * Column definitions for the allocation modal
   */
  columns: StatementColumn[]

  /**
   * Save function for normal (non-allocation) saves
   * Receives the changed grid rows
   */
  onSave: (budgetData: any) => Promise<void>

  /**
   * Entity type - determines which converters to use
   * - 'account': Uses account budget converters
   * - 'formula': Uses formula/driver data converters
   */
  entityType: AllocationEntityType
}

export const useBudgetAllocation = <TRow extends WithBalanceRegistries>({
  gridApi,
  columns,
  onSave,
  entityType
}: UseBudgetAllocationProps<TRow>) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Select converters based on entity type
  const converters = useMemo(() => {
    switch (entityType) {
      case 'account':
        return {
          toAllocationRows: convertAccountRowToAllocationRows as any,
          toBudgetData: convertMonthlyBudgetsToAccountData
        }
      case 'formula':
        return {
          toAllocationRows: convertDriverDataToAllocationRows as any,
          toBudgetData: convertMonthlyBudgetsToFormulaData
        }
      default:
        throw new Error(`Unknown entity type: ${entityType}`)
    }
  }, [entityType])

  // Collect changed rows from grid
  const collectChangedRows = useCallback((): TRow[] => {
    if (!gridApi) return []

    const changedRows: TRow[] = []
    gridApi.forEachNode(node => {
      if (!node.data) return

      const { newBudgets, newActuals } = node.data

      // Check if there's actual data in elements or groups
      const hasBudgetData = hasRegistryData(newBudgets)
      const hasActualData = hasRegistryData(newActuals)

      if (hasBudgetData || hasActualData) {
        changedRows.push(node.data)
      }
    })

    return changedRows
  }, [gridApi])

  // Check if rows have group (aggregated) changes that require allocation
  const hasGroupChanges = useCallback((rows: TRow[]): boolean => {
    return rows.some(row => {
      const hasBudgetGroups = hasRegistryGroups(row.newBudgets)
      const hasActualGroups = hasRegistryGroups(row.newActuals)
      return hasBudgetGroups || hasActualGroups
    })
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const AllocationModal = useMemo(
    () =>
      isModalOpen ? (
        <BudgetAllocationModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSave={onSave}
          gridApi={gridApi}
          columns={columns}
          convertToAllocationRows={converters.toAllocationRows}
          convertToBudgetRows={converters.toBudgetData}
        />
      ) : null,
    [isModalOpen, gridApi, columns, handleModalClose, onSave, converters]
  )

  return {
    AllocationModal,
    isModalOpen,
    setIsModalOpen,
    collectChangedRows,
    hasGroupChanges
  }
}
